import { ReactNode } from 'react';
import { ArrowUpRight, Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useUpgrade } from './useUpgrade';

/**
 * An inline card that says what Pro adds here, with one way to get it.
 *
 * It sits above the content, never instead of it: the vendor keeps seeing
 * the page they came to, and the card explains the one thing it will not do
 * yet. Monochrome, outlined — the only emphasis is the words.
 */
export function UnlockCallout({
  title,
  body,
  why,
  reason,
  ctaLabel,
  action,
  className,
  testId = 'unlock-callout',
}: {
  title?: ReactNode;
  /** What the vendor gets on Pro, in a sentence. */
  body?: ReactNode;
  /** An optional line on why it is limited — honest, short. */
  why?: ReactNode;
  /** The limit or feature behind it, passed to the upgrade page. */
  reason?: string;
  ctaLabel?: ReactNode;
  /** Replaces the default upgrade button (a page with its own route to the upgrade). */
  action?: ReactNode;
  className?: string;
  testId?: string;
}) {
  const { t } = useTranslation();
  const upgrade = useUpgrade();

  return (
    <div
      data-testid={testId}
      className={cn('flex flex-col gap-3 rounded-xl border bg-card p-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6', className)}
    >
      <div className="flex min-w-0 items-start gap-3">
        <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg border bg-muted text-muted-foreground">
          <Lock className="size-3.5" aria-hidden />
        </span>
        <div className="min-w-0">
          <p className="text-sm font-medium">{title ?? t('FREE_PLAN.CALLOUT.TITLE')}</p>
          <p className="mt-0.5 text-sm leading-relaxed text-muted-foreground">{body ?? t('FREE_PLAN.CALLOUT.BODY')}</p>
          {why && <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">{why}</p>}
        </div>
      </div>
      {action ?? (
        <Button variant="outline" size="sm" className="h-8 shrink-0 self-start sm:self-center" onClick={() => upgrade(reason)} data-testid={`${testId}-cta`}>
          {ctaLabel ?? t('FREE_PLAN.UNLOCK_WITH_PRO')}
          <ArrowUpRight data-icon="inline-end" className="rtl:-scale-x-100" />
        </Button>
      )}
    </div>
  );
}
